import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, distinctUntilChanged, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { IFile } from './bucket.service';

export type EmbeddingState = 'queued' | 'processing' | 'ready' | 'failed' | 'pending';

/** One file's indexing state as `POST /files/embedding-status` reports it. */
export interface EmbeddingStatus {
  uuid: string;
  state: EmbeddingState;
  /** Split PDFs only: pages read so far / in total. */
  pages_done?: number;
  pages_total?: number;
  /** Whole-file jobs waiting in the queue: 1 = next of this organisation. */
  queue_position?: number;
  /** Ready, but some pages could not be read. */
  warning?: string;
  error?: string;
}

const POLL_MS = 4000;
/** A file listed as "pending" right after its upload may simply not be in
 *  the queue yet (the web pod enqueues after the storage upload). */
const FRESH_UPLOAD_MS = 2 * 60 * 1000;

/**
 * Indexing state of the files the knowledge base currently shows, kept fresh
 * by polling while any of them is still being processed.
 *
 * The table rows never change for this: each status chip subscribes to its
 * own file here, so polling does not rebuild `app-table` (CLAUDE.md rule 18).
 * Polling stops when nothing is left in progress, pauses while the tab is
 * hidden, and ends when the table goes away (`stop`).
 */
@Injectable({ providedIn: 'root' })
export class EmbeddingStatusService implements OnDestroy {
  private readonly statuses$ = new BehaviorSubject<Record<string, EmbeddingStatus>>({});
  private watched: IFile[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private request?: Subscription;
  private readonly onVisibility = () => {
    if (!document.hidden) this.schedule(0);
  };

  constructor(private http: HttpClient) {
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.stop();
  }

  /** The state of one file, seeded from its listing row until the server answers. */
  status$(file: IFile): Observable<EmbeddingStatus> {
    return this.statuses$.pipe(
      map((all) => all[file.uuid] || this.seed(file)),
      distinctUntilChanged(
        (a, b) =>
          a.state === b.state &&
          a.pages_done === b.pages_done &&
          a.pages_total === b.pages_total &&
          a.queue_position === b.queue_position &&
          a.warning === b.warning &&
          a.error === b.error
      )
    );
  }

  /** Follow these files (the table's current list) until they are settled. */
  watch(files: IFile[]): void {
    this.watched = (files || []).filter((f) => !!f?.uuid);
    // A settled answer the listing now contradicts (file re-uploaded or
    // re-indexed since) is dropped, so the file is followed again. A stale
    // row costs one extra poll at worst.
    const cache = { ...this.statuses$.value };
    let dropped = false;
    for (const file of this.watched) {
      const cached = cache[file.uuid];
      if (cached && (cached.state === 'ready' || cached.state === 'failed')
          && this.seed(file).state !== cached.state) {
        delete cache[file.uuid];
        dropped = true;
      }
    }
    if (dropped) this.statuses$.next(cache);
    this.schedule(0);
  }

  stop(): void {
    this.watched = [];
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.request?.unsubscribe();
  }

  private seed(file: IFile): EmbeddingStatus {
    if (file.status_embedding) {
      return { uuid: file.uuid, state: 'ready', warning: file.embedding_error || undefined };
    }
    if (file.embedding_error) {
      return { uuid: file.uuid, state: 'failed', error: file.embedding_error };
    }
    return { uuid: file.uuid, state: 'pending' };
  }

  private settled(file: IFile): boolean {
    const status = this.statuses$.value[file.uuid] || this.seed(file);
    if (status.state === 'ready' || status.state === 'failed') return true;
    if (status.state !== 'pending') return false;
    // "pending" is final only once the server said so and the upload is not
    // fresh — otherwise keep asking until its job shows up.
    const uploaded = Date.parse(file.uploaded_at || '');
    const fresh = !isNaN(uploaded) && Date.now() - uploaded < FRESH_UPLOAD_MS;
    return !!this.statuses$.value[file.uuid] && !fresh;
  }

  private schedule(delayMs: number): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (!this.watched.some((f) => !this.settled(f))) return;
    this.timer = setTimeout(() => this.poll(), delayMs);
  }

  private poll(): void {
    this.timer = null;
    if (document.hidden) return; // resumed by visibilitychange
    const open = this.watched.filter((f) => !this.settled(f)).slice(0, 500);
    if (!open.length) return;
    this.request?.unsubscribe();
    this.request = this.http
      .post<{ data: EmbeddingStatus[] }>(`${environment.filesBackend}/files/embedding-status`, {
        file_uuids: open.map((f) => f.uuid),
      })
      .subscribe({
        next: (res) => {
          const next = { ...this.statuses$.value };
          for (const status of res?.data || []) next[status.uuid] = status;
          this.statuses$.next(next);
          this.schedule(POLL_MS);
        },
        // A failed poll is no reason to stop following the files.
        error: () => this.schedule(POLL_MS * 2),
      });
  }
}
